import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Star, Brain, Zap } from 'lucide-react';
import { hideNativeTabBar, showNativeTabBar } from '../../services/api';
import './OnboardingModal.css';

const STEPS = [
  {
    id: 'welcome',
    tag: 'WELCOME',
    icon: null, // 로고 이미지 사용
    illustrationClass: 'ob-illust--welcome',
    title: '코인특공대에\n오신 것을 환영해요!',
    description: 'AI가 매일 수백 개 주식을 분석해\n투자 점수와 전략을 제공합니다.',
    detail: null,
  },
  {
    id: 'score',
    tag: 'AI SCORE',
    icon: <Brain size={40} />,
    illustrationClass: 'ob-illust--score',
    title: 'AI 점수란 무엇인가요?',
    description: '기술적 분석, 펀더멘탈, 뉴스, 시장 심리를\n종합한 0~100점 투자 지표예요.',
    detail: [
      { range: '70점 이상', label: '적극 매수', color: '#059669' },
      { range: '40 – 69점', label: '보유 관망', color: '#2563eb' },
      { range: '40점 미만', label: '주의 / 매도 고려', color: '#dc2626' },
    ],
  },
  {
    id: 'favorites',
    tag: 'FAVORITES',
    icon: <Star size={40} fill="currentColor" />,
    illustrationClass: 'ob-illust--favorites',
    title: '관심종목을\n추가해보세요',
    description: '검색 후 ★ 아이콘을 탭하면\n관심종목으로 등록됩니다.',
    detail: null,
    cta: '매일 아침 AI가 관심종목을 재분석해 점수를 업데이트해요.',
  },
];

const slideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { type: 'tween', duration: 0.32, ease: [0.32, 0.72, 0, 1] },
  },
  exit: (direction) => ({
    x: direction > 0 ? '-100%' : '100%',
    opacity: 0,
    transition: { duration: 0.2 },
  }),
};

const OnboardingModal = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    if (isOpen) hideNativeTabBar();
    else showNativeTabBar();
    return () => showNativeTabBar();
  }, [isOpen]);

  const step = STEPS[currentStep];
  const isLast = currentStep === STEPS.length - 1;

  const goNext = () => {
    if (isLast) {
      onClose();
    } else {
      setDirection(1);
      setCurrentStep((s) => s + 1);
    }
  };

  const goSkip = () => {
    onClose();
  };

  const handleDotClick = (idx) => {
    setDirection(idx > currentStep ? 1 : -1);
    setCurrentStep(idx);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="ob-root fixed inset-0 z-[10500] bg-white flex flex-col overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* 상단 헤더 */}
          <div className="ob-header">
            <span className="ob-step-tag">{step.tag}</span>
            <button className="ob-skip-btn" onClick={goSkip}>
              건너뛰기
            </button>
          </div>

          {/* 슬라이드 영역 */}
          <div className="ob-slide-wrap">
            <AnimatePresence custom={direction} mode="wait">
              <motion.div
                key={step.id}
                className="ob-slide"
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                {/* 일러스트 카드 */}
                <div className={`ob-illust ${step.illustrationClass}`}>
                  {step.id === 'welcome' ? (
                    <img src="/logo.png" alt="코인특공대 로고" className="ob-logo" />
                  ) : (
                    <div className="ob-illust-icon">{step.icon}</div>
                  )}
                </div>

                {/* 텍스트 */}
                <div className="ob-text-block">
                  <h2 className="ob-title">{step.title}</h2>
                  <p className="ob-desc">{step.description}</p>

                  {/* AI 점수 단계 설명 */}
                  {step.detail && (
                    <div className="ob-score-rows">
                      {step.detail.map((d) => (
                        <div key={d.range} className="ob-score-row">
                          <span className="ob-score-range">{d.range}</span>
                          <span className="ob-score-label" style={{ color: d.color }}>
                            {d.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* CTA 메모 */}
                  {step.cta && (
                    <div className="ob-cta-note">
                      <Zap size={13} className="ob-cta-icon" />
                      <span>{step.cta}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* 하단 컨트롤 */}
          <div className="ob-footer">
            {/* Dot indicator */}
            <div className="ob-dots">
              {STEPS.map((_, idx) => (
                <button
                  key={idx}
                  className={`ob-dot ${idx === currentStep ? 'ob-dot--active' : ''}`}
                  onClick={() => handleDotClick(idx)}
                />
              ))}
            </div>

            {/* 버튼 */}
            <button className="ob-next-btn" onClick={goNext}>
              {isLast ? '시작하기' : '다음'}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OnboardingModal;
